import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import moment from "moment";
import { buildChecklistGroups } from "../const/surveyorReportDefs";

const UnitechHeader = "../img/Surveyor_header.png";
const UnitechFooter = "../img/IR_Footer.png";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "AptosBold",
  normalFont: "AptosNormal",
};

export const config = {
  margin: 15,
  borderMargin: 15,
  headerimgHeight: 70,
  footerimgHeight: 0,
  pageNoHeight: 15,
  borderGap: 2,
  fontSize: 10,
  fontSizeSm: 10,
  fontSizeLg: 12,
  fontSizeTitle: 15,
  cellPadding: 4,
  sectionGap: 0,
  borderFooterGap: 15,

  get contentTop() {
    return this.headerimgHeight + this.borderGap + this.borderMargin + 15;
  },
  get contentBottom() {
    return this.footerimgHeight + this.pageNoHeight + this.borderGap + this.borderMargin + 5;
  },
};

const startNewPage = () => {
  layout.doc.addPage();
  layout.y = config.contentTop;
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: false,
});

const getTableMargins = () => ({
  left: config.margin,
  right: config.margin,
  top: config.contentTop,
  bottom: config.contentBottom,
});

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },
  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },
  advance(delta) {
    this.y += delta;
  },
  ensureSpace(needed = 30) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

// FONT LOADER
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  const fontResponse = await fetch(fontUrl);
  const fontBuffer = await fontResponse.arrayBuffer();
  const fontBase64 = arrayBufferToBase64(fontBuffer);
  pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
  pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
};

// NEW — SIGNATURE IMAGE LOADER (only addition of this kind; mirrors the font loader pattern above)
const loadImageAsBase64 = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const contentType = response.headers.get("content-type") || "";
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    const format = contentType.includes("jpeg") || contentType.includes("jpg") ? "JPEG" : "PNG";
    return { dataUrl: `data:${contentType || "image/png"};base64,${base64}`, format };
  } catch (err) {
    console.log("signature image load error", err);
    return null;
  }
};

const addHeaderFooter = (doc, docNumber) => {
  const marginX = 0;
  const width = layout.pageWidth;

  if (config.headerimgHeight > 0) {
    doc.addImage(UnitechHeader, "PNG", marginX, 0, width, config.headerimgHeight);
  }
  if (config.footerimgHeight > 0) {
    doc.addImage(
      UnitechFooter,
      "PNG",
      marginX,
      layout.pageHeight - config.footerimgHeight - 3,
      width,
      config.footerimgHeight
    );
  }
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
};

const addPageNumbers = (doc, totalPages, docNumber) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addHeaderFooter(doc, docNumber);

    const pageNoY = layout.pageHeight - config.footerimgHeight - config.borderFooterGap;

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSizeSm);
    doc.setTextColor(0);
    doc.text("DYC/FMT-MR/45 Rev.00 ; Date-24/05/2023", config.borderMargin, pageNoY);
    doc.text(
      `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`,
      layout.pageWidth - config.margin - 80,
      pageNoY
    );
  }
};

// SECTION HEADING — gray bar, e.g. "Reference Documents"
const addSectionHeading = (title) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   [[title]],
    theme:  "grid",
    styles: {
      ...getHeaderStyles(),
      cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
    },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(0);
};

// LABEL / VALUE TABLE — e.g. Surveyor Name | Rohit Barge
const addLabelValueTable = (rows, labelWidthRatio = 0.32) => {
  const labelW = layout.contentWidth * labelWidthRatio;
  const valueW = layout.contentWidth - labelW;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: rows.map(([label, value]) => [
      { content: label, styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: value || "", styles: getBaseStyles() },
    ]),
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: labelW }, 1: { cellWidth: valueW } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(0);
};

// FREE-TEXT BLOCK — Description of activities
const addTextBlock = (title, text) => {
  addSectionHeading(title);
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [[text || ""]],
    theme: "grid",
    styles: { ...getBaseStyles(), minCellHeight: 30, valign: "top" },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

const addItemTable = (itemRows) => {

  const head = [[
    { content: "Sr.No", styles: getHeaderStyles() },
    { content: "Item Description", styles: getHeaderStyles() },
    { content: "Competence Scope", styles: getHeaderStyles() },
    { content: "Vendor Name", styles: getHeaderStyles() },
  ]];

  const rows = itemRows?.length ? itemRows : [{}];

  const srColW = 40;
  const descColW = layout.contentWidth * 0.4;
  const scopeColW = layout.contentWidth * 0.3;
  const vendColW = layout.contentWidth - srColW - descColW - scopeColW;

  const body = rows.map((row, idx) => [
    { content: idx + 1, styles: getBaseStyles() },
    { content: row.itemDescription || "", styles: getBaseStyles() },
    { content: row.competenceScope || "", styles: getBaseStyles() },
    { content: row.vendorName || "", styles: getBaseStyles() },
  ]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: {
      0: { cellWidth: srColW },
      1: { cellWidth: descColW },
      2: { cellWidth: scopeColW },
      3: { cellWidth: vendColW },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// Builds the body rows for ONE checklist group (used inside a dedicated
// per-group table so the whole group can be kept off a page break).
const buildGroupBody = (group, getVal, colWidths) => {
  const body = [];
  let firstRow = true;

  group.defs.forEach((def) => {
    if (def.type === "yesNoNAWithOptions") {
      const v = getVal(def.key);
      const answerText = ["Yes", "No", "NA"]
        .map((opt) => `${v === opt ? "\u2611" : "\u2610"} ${opt}`)
        .join("   ");

      body.push(
        [
          firstRow
            ? { content: group.no, rowSpan: group.rowCount, styles: { ...getBaseStyles(), halign: "center", valign: "top" } }
            : null,
          { content: def.label, styles: getBaseStyles() },
          { content: answerText, styles: { ...getBaseStyles(), halign: "center" } },
        ].filter(Boolean)
      );
      firstRow = false;

     const selected = getVal(def.optionKey);
      def.options.forEach((opt) => {
        body.push([
          { content: opt.label, styles: getBaseStyles() },
          { content: selected === opt.value ? "\u2611" : "\u2610", styles: { ...getBaseStyles(), halign: "center" } },
        ]);
      });
    } else {
      let answerText = "";
      if (def.type === "yesNoNA") {
        const v = getVal(def.key);
        answerText = ["Yes", "No", "NA"]
          .map((opt) => `${v === opt ? "\u2611" : "\u2610"} ${opt}`)
          .join("   ");
      } else {
        answerText = getVal(def.key) || "-";
      }

      body.push(
        [
          firstRow
            ? { content: group.no, rowSpan: group.rowCount, styles: { ...getBaseStyles(), halign: "center", valign: "top" } }
            : null,
          { content: def.label, styles: getBaseStyles() },
          { content: answerText, styles: { ...getBaseStyles(), halign: "center" } },
        ].filter(Boolean)
      );
      firstRow = false;
    }
  });

  return body;
};

// Checklist — no header row (Sr./Question/Response removed per request), and
// each numbered group is drawn as its own table with pageBreak:'avoid' so a
// group's rows are NEVER split across two pages — the whole group jumps to
// the next page together if it doesn't fit on the current one.
const addChecklistTable = (questions) => {
  addSectionHeading("Checklist");

  const getVal = (key) => questions.find((q) => q.key === key)?.value || "";

  const srColW = 25;
  const qColW = layout.contentWidth * 0.6;
  const respColW = layout.contentWidth - srColW - qColW;

  const groups = buildChecklistGroups();

  groups.forEach((group) => {
    const body = buildGroupBody(group, getVal, { srColW, qColW, respColW });

    autoTable(layout.doc, {
      startY: layout.y,
      margin: getTableMargins(),
      body,
      theme: "grid",
      styles: getBaseStyles(),
      pageBreak: "avoid", // keeps this whole group together, moves it to next page if needed
      columnStyles: {
        0: { cellWidth: srColW },
        1: { cellWidth: qColW },
        2: { cellWidth: respColW },
      },
      didDrawPage: () => { layout.y = config.contentTop; },
    });
    layout.afterTable(0);
  });

  layout.advance(config.sectionGap);
};

// Signature cell — blank bordered box, now draws the signature image on top
// (via _sig on the cell + didDrawCell in the tables below) when one is available.
// Nothing about the cell's label/width/height/spacing has changed.
const addSignatureRow = (label, dateValue, sigImg) => ([
  { content: label, styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, minCellHeight: 45 } },
  { content: "", styles: { ...getBaseStyles(), minCellHeight: 45 }, _sig: sigImg },
  { content: "Date:", styles: { ...getHeaderStyles(), minCellHeight: 45 } },
  { content: dateValue ? moment(dateValue).format("DD/MM/YYYY") : "", styles: { ...getBaseStyles(), minCellHeight: 45 } },
]);

const addDeclarationBlock = (inputStates, monitorSig, surveyorSig, technicalManagerSig) => {
  addSectionHeading("Declaration (To be completed by the monitor)");

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [[
      {
        content:
          "I confirm that I have conducted an activity monitoring exercise on the individual named below, in accordance with procedure DYC/QP-IB/04. I recommend actions as described above.",
        styles: { ...getBaseStyles(), minCellHeight: 24 },
      },
    ]],
    theme: "grid",
    styles: getBaseStyles(),
    pageBreak: "avoid",
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();

  const labelColW = layout.contentWidth * 0.32;
  const sigColW = layout.contentWidth * 0.38;
  const dateLabelColW = 40;
  const dateColW = layout.contentWidth - labelColW - sigColW - dateLabelColW;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      addSignatureRow("Signed (Monitor):", inputStates?.monitoringSignedDate, monitorSig),
      [
        { content: "Remarks by Surveyor:", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: inputStates?.remarksBySurveyor || "", colSpan: 3, styles: getBaseStyles() },
      ],
      addSignatureRow("Signed (Surveyor):", inputStates?.surveyorSignedDate, surveyorSig),
      addSignatureRow("Signed Technical Manager:", inputStates?.technicalManagerSignedDate, technicalManagerSig),
    ],
    theme: "grid",
    styles: getBaseStyles(),
    pageBreak: "avoid",
    columnStyles: {
      0: { cellWidth: labelColW },
      1: { cellWidth: sigColW },
      2: { cellWidth: dateLabelColW },
      3: { cellWidth: dateColW },
    },
    didDrawCell: (data) => {
      const sig = data.cell.raw && data.cell.raw._sig;
      if (sig && data.column.index === 1) {
        const maxW = data.cell.width - 6;
        const maxH = data.cell.height - 6;
        const w = Math.min(maxW, 90);
        const h = Math.min(maxH, 28);
        layout.doc.addImage(sig.dataUrl, sig.format, data.cell.x + 3, data.cell.y + (data.cell.height - h) / 2, w, h);
      }
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

const addObservationBlock = (inputStates, monitorSig, technicalManagerSig) => {
  layout.ensureSpace(60);
  layout.afterTable(4);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        {
          content:"If there are observations in first monitoring and reassessment done on that basis then below table to fill accordingly.",
          styles: { ...getBaseStyles(), },
        },
      ],
      [
        {
          content: `Observation / Comments / Action implemented and its effectiveness checked on Date: \nFound Satisfactory / Unsatisfactory\nIf implementation /effectiveness of action found unsatisfactory /insufficient further action suggested`,
          styles: { ...getBaseStyles(), minCellHeight: 40, valign: "top" },
        },
      ],
    ],
    theme: "grid",
    styles: getBaseStyles(),
    pageBreak: "avoid",
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();

  const labelColW = layout.contentWidth * 0.32;
  const sigColW = layout.contentWidth * 0.38;
  const dateLabelColW = 40;
  const dateColW = layout.contentWidth - labelColW - sigColW - dateLabelColW;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      addSignatureRow("Signed (Monitor):", inputStates?.monitoringSignedDate, monitorSig),
      addSignatureRow("Signed (Technical Manager):", inputStates?.technicalManagerSignedDate, technicalManagerSig),
    ],
    theme: "grid",
    styles: getBaseStyles(),
    pageBreak: "avoid",
    columnStyles: {
      0: { cellWidth: labelColW },
      1: { cellWidth: sigColW },
      2: { cellWidth: dateLabelColW },
      3: { cellWidth: dateColW },
    },
    didDrawCell: (data) => {
      const sig = data.cell.raw && data.cell.raw._sig;
      if (sig && data.column.index === 1) {
        const maxW = data.cell.width - 6;
        const maxH = data.cell.height - 6;
        const w = Math.min(maxW, 90);
        const h = Math.min(maxH, 28);
        layout.doc.addImage(sig.dataUrl, sig.format, data.cell.x + 3, data.cell.y + (data.cell.height - h) / 2, w, h);
      }
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

export async function generateSurveyorMonitoringReportPDF(
  inputStates,
  employee,
  otherData,
  referenceDocs,
  itemRows,
  questions,
  employeeOptions,
  setDownloadPdfloading,
) {
  try {
    setDownloadPdfloading(true);

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    const monitorSignatureUrl = employeeOptions?.find((o) => o.id === inputStates?.monitorId)?.signature || "";
    const technicalManagerSignatureUrl = employeeOptions?.find((o) => o.id === inputStates?.SignedTechnicalManagerId)?.signature || "";
    const surveyorSignatureUrl = employee?.signature || "";

    const [, , monitorSig, surveyorSig, technicalManagerSig] = await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
      loadImageAsBase64(monitorSignatureUrl),
      loadImageAsBase64(surveyorSignatureUrl),
      loadImageAsBase64(technicalManagerSignatureUrl),
    ]);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    layout.init(doc);
    const addMonitorDetailsTable = () => {
      autoTable(layout.doc, {
        startY: layout.y,
        margin: getTableMargins(),
        body: [
          ["Surveyor Name", employee?.name || "", "Designation", otherData?.hrDesignation || ""],
          ["Monitor Name", inputStates?.monitorName || "", "Designation", inputStates?.monitorDesignation || ""],
        ],
        theme: "grid",
        styles: getBaseStyles(),
        columnStyles: {
          0: { cellWidth: layout.contentWidth * 0.18, font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
          1: { cellWidth: layout.contentWidth * 0.32 },
          2: { cellWidth: layout.contentWidth * 0.18, font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
          3: { cellWidth: layout.contentWidth * 0.32 },
        },
        didDrawPage: () => {
          layout.y = config.contentTop;
        },
      });
      layout.afterTable(0);
    };
    addMonitorDetailsTable();


    addLabelValueTable([
      ["Client / Purchaser Name as Per PO", inputStates?.purchaseClientName],
      ["Project Name", inputStates?.projectName],
      ["Inspected Item Description", inputStates?.inspectedItemDescription],
      ["DYC Control number", inputStates?.dycControlNumber],
      [
        "Date(s) of Monitoring",
        inputStates?.monitoringDate ? moment(inputStates.monitoringDate).format("DD/MM/YYYY") : "",
      ],
    ]);

    addSectionHeading(" ");
    addSectionHeading("Reference Documents");
    addLabelValueTable((referenceDocs || []).map((r) => [r.label, r.value]));

    addTextBlock("Description of activities performed by surveyor", inputStates?.descriptionOfActivities);

    addItemTable(itemRows);

    addChecklistTable(questions);
    startNewPage();
    addDeclarationBlock(inputStates, monitorSig, surveyorSig, technicalManagerSig);
    addObservationBlock(inputStates, monitorSig, technicalManagerSig);

    addPageNumbers(doc, doc.getNumberOfPages(), inputStates?.id ? `SMR-${inputStates.id}` : "");

    doc.save(`Surveyor_Monitoring_Report_${inputStates?.dycControlNumber || inputStates?.id || ""}.pdf`);
  } catch (error) {
    console.error("Surveyor monitoring report PDF generation error:", error);
  } finally {
    setDownloadPdfloading(false);
  }
}